import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import IconLoading from '@/assets/icons/icon-loading.svg'
import { PageHeader } from '@/components/layout/page/page-header.tsx'
import { Page } from '@/components/layout/page/page.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { $currentCreator } from '@/models/creators.ts'
import {
  $newSubsList,
  getNewSubsListFx,
  removeNewSubsListFx,
  updateNewSubsListFx,
} from '@/models/lists/newSubs.ts'

const NO_LIST_ID = '-1'

export const SubListPage = () => {
  const activeCreator = useUnit($currentCreator)
  const selected = useUnit($newSubsList)
  const newSubsLoading = useUnit(getNewSubsListFx.pending)
  const isUpdating = useUnit(updateNewSubsListFx.pending)
  const isRemoving = useUnit(removeNewSubsListFx.pending)
  const isLoading = newSubsLoading || isUpdating || isRemoving

  const onChange = useCallback(async (id: string) => {
    if (id === NO_LIST_ID) {
      await removeNewSubsListFx()
    } else {
      await updateNewSubsListFx(id)
    }
  }, [])

  return (
    <Page>
      <PageHeader
        title="New Subs List"
        description={
          <span className="flex items-center gap-2">
            <span className="truncate">
              Automatically adds every new fan to the chosen list
            </span>
          </span>
        }
      />
      <div className="flex max-w-xs">
        <Select
          value={selected?.listId ? String(selected.listId) : NO_LIST_ID}
          onValueChange={onChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full">
            {selected?.listId ? (
              <SelectValue />
            ) : isLoading ? (
              <IconLoading className="h-4 w-4 animate-spin" />
            ) : (
              'None'
            )}
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {!activeCreator && (
              <span className="flex items-center justify-center gap-2 px-2">
                <IconLoading className="h-4 w-4 animate-spin" />
                <span>Loading...</span>
              </span>
            )}
            <SelectGroup>
              {activeCreator && (
                <SelectItem
                  key="None"
                  value={NO_LIST_ID}
                  disabled={!selected?.listId}
                >
                  None
                </SelectItem>
              )}
              {activeCreator?.lists
                ?.filter((list) => list.type === 'custom')
                .map((option) => {
                  return (
                    <SelectItem
                      key={option.id}
                      value={String(option.id)}
                      disabled={option.id === selected?.listId?.toString()}
                    >
                      {option.name}
                    </SelectItem>
                  )
                })}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </Page>
  )
}
